import { useState } from 'react';
import { useAuth } from '@clerk/clerk-react';
import { useQuery } from '@tanstack/react-query';
import AppLayout from '../components/AppLayout';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Search, Database, AlertTriangle, CheckCircle, XCircle } from 'lucide-react';
import { API_BASE } from '../lib/api';

interface DebugMoMData {
  debug: {
    sessionId: string;
    title: string;
    coachType: string;
    createdAt: string;
    updatedAt: string;
    hasMomData: boolean;
    momDataLength: number;
    momDataStatus: string;
    parseError?: string;
  };
  rawMomData: any;
  parsedMomData: any;
  analysis: {
    isNull: boolean;
    isEmpty: boolean;
    isEmptyObject: boolean;
    hasKeyTakeaways: boolean;
    hasActionLedger: boolean;
    hasTitle: boolean;
  };
}

interface SessionOverview {
  debug: {
    userId: string;
    summary: {
      totalSessions: number;
      withMomData: number;
      withoutMomData: number;
      emptyObjects: number;
      emptyStrings: number;
      nullValues: number;
      withData: number;
    };
  };
  sessions: Array<{
    id: string;
    title: string;
    coachType: string;
    updatedAt: string;
    hasMomData: boolean;
    momDataLength: number;
    momDataStatus: string;
  }>;
}

export default function MoMDebugPage() {
  const { getToken, isSignedIn } = useAuth();
  const [selectedSessionId, setSelectedSessionId] = useState<string>('');
  const [searchTerm, setSearchTerm] = useState<string>('');

  // Fetch overview of all sessions
  const { data: overview, isLoading: overviewLoading } = useQuery<SessionOverview>({
    queryKey: ['debug-mom-overview'],
    enabled: isSignedIn,
    queryFn: async () => {
      const token = await getToken();
      if (!token) throw new Error('Not authenticated');
      
      const response = await fetch(`${API_BASE}/debug-mom`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (!response.ok) throw new Error('Failed to fetch debug data');
      return response.json();
    }
  });

  // Fetch specific session details
  const { data: sessionDetail, isLoading: detailLoading } = useQuery<DebugMoMData>({
    queryKey: ['debug-mom-session', selectedSessionId],
    enabled: isSignedIn && Boolean(selectedSessionId),
    queryFn: async () => {
      const token = await getToken();
      if (!token) throw new Error('Not authenticated');
      
      const response = await fetch(`${API_BASE}/debug-mom?sessionId=${selectedSessionId}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (!response.ok) throw new Error('Failed to fetch session debug data');
      return response.json();
    }
  });

  const filteredSessions = overview?.sessions?.filter(session => 
    session.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
    session.id.toLowerCase().includes(searchTerm.toLowerCase())
  ) || [];

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'HAS_DATA':
        return <CheckCircle className="h-4 w-4 text-green-500" />;
      case 'NULL':
        return <XCircle className="h-4 w-4 text-red-500" />;
      case 'EMPTY_OBJECT':
      case 'EMPTY_STRING':
        return <AlertTriangle className="h-4 w-4 text-yellow-500" />;
      default:
        return <XCircle className="h-4 w-4 text-gray-400" />;
    }
  };

  return (
    <AppLayout currentPage="insights-hub">
      <div className="min-h-screen px-4 py-8 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-7xl space-y-8">
          {/* Header */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-white shadow-sm border border-red-200">
                <Database className="h-6 w-6 text-red-600" strokeWidth={1.5} />
              </div>
              <div>
                <h1 className="font-display text-3xl font-bold text-slate-900 tracking-tight">MoM Debug Panel</h1>
                <p className="text-base text-slate-600 leading-relaxed">Diagnostic tool for MoM generation issues</p>
              </div>
            </div>
          </div>

          {/* Summary Stats */}
          {overview && (
            <Card className="bg-white rounded-3xl border border-slate-200 shadow-sm">
              <CardContent className="p-6">
                <h2 className="font-display text-xl font-semibold text-slate-900 mb-4">Session Summary</h2>
                <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-4">
                  <div className="text-center">
                    <div className="text-2xl font-bold text-blue-600">{overview.debug.summary.totalSessions}</div>
                    <div className="text-xs text-slate-500">Total Sessions</div>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl font-bold text-green-600">{overview.debug.summary.withData}</div>
                    <div className="text-xs text-slate-500">With Data</div>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl font-bold text-red-600">{overview.debug.summary.nullValues}</div>
                    <div className="text-xs text-slate-500">Null Values</div>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl font-bold text-yellow-600">{overview.debug.summary.emptyObjects}</div>
                    <div className="text-xs text-slate-500">Empty Objects</div>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl font-bold text-yellow-600">{overview.debug.summary.emptyStrings}</div>
                    <div className="text-xs text-slate-500">Empty Strings</div>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl font-bold text-slate-600">{overview.debug.summary.withoutMomData}</div>
                    <div className="text-xs text-slate-500">No MoM Data</div>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl font-bold text-green-600">{overview.debug.summary.withMomData}</div>
                    <div className="text-xs text-slate-500">Has MoM Data</div>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          <div className="grid gap-6 lg:grid-cols-2">
            {/* Sessions List */}
            <Card className="bg-white rounded-3xl border border-slate-200 shadow-sm">
              <CardContent className="p-6">
                <div className="flex items-center gap-3 mb-4">
                  <h2 className="font-display text-xl font-semibold text-slate-900">Recent Sessions</h2>
                  <div className="flex-1">
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400" />
                      <Input 
                        placeholder="Search sessions..." 
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="pl-10"
                      />
                    </div>
                  </div>
                </div>
                
                {overviewLoading ? (
                  <div className="text-center py-8 text-slate-500">Loading sessions...</div>
                ) : (
                  <div className="space-y-2 max-h-96 overflow-y-auto">
                    {filteredSessions.map((session) => (
                      <div 
                        key={session.id}
                        className={`p-3 rounded-lg border cursor-pointer transition-colors ${
                          selectedSessionId === session.id 
                            ? 'border-blue-200 bg-blue-50' 
                            : 'border-slate-200 hover:border-slate-300 hover:bg-slate-50'
                        }`}
                        onClick={() => setSelectedSessionId(session.id)}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex-1 min-w-0">
                            <div className="font-medium text-sm text-slate-900 truncate">{session.title}</div>
                            <div className="text-xs text-slate-500">{session.id}</div>
                          </div>
                          <div className="flex items-center gap-2">
                            {getStatusIcon(session.momDataStatus)}
                            <span className="text-xs text-slate-500">{session.momDataLength || 0}b</span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Session Details */}
            <Card className="bg-white rounded-3xl border border-slate-200 shadow-sm">
              <CardContent className="p-6">
                <h2 className="font-display text-xl font-semibold text-slate-900 mb-4">Session Details</h2>
                
                {!selectedSessionId ? (
                  <div className="text-center py-8 text-slate-500">Select a session to view details</div>
                ) : detailLoading ? (
                  <div className="text-center py-8 text-slate-500">Loading session details...</div>
                ) : sessionDetail ? (
                  <div className="space-y-4">
                    {/* Basic Info */}
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <span className="font-medium text-slate-700">Status:</span>
                        <div className="flex items-center gap-2 mt-1">
                          {getStatusIcon(sessionDetail.debug.momDataStatus)}
                          <span className="text-slate-600">{sessionDetail.debug.momDataStatus}</span>
                        </div>
                      </div>
                      <div>
                        <span className="font-medium text-slate-700">Data Size:</span>
                        <div className="text-slate-600 mt-1">{sessionDetail.debug.momDataLength || 0} bytes</div>
                      </div>
                      <div>
                        <span className="font-medium text-slate-700">Coach:</span>
                        <div className="text-slate-600 mt-1">{sessionDetail.debug.coachType}</div>
                      </div>
                      <div>
                        <span className="font-medium text-slate-700">Updated:</span>
                        <div className="text-slate-600 mt-1">{new Date(sessionDetail.debug.updatedAt).toLocaleDateString()}</div>
                      </div>
                    </div>

                    {/* Analysis */}
                    <div className="border-t pt-4">
                      <span className="font-medium text-slate-700 block mb-2">Analysis:</span>
                      <div className="grid grid-cols-2 gap-2 text-xs">
                        <div className={`flex items-center gap-2 ${sessionDetail.analysis.hasTitle ? 'text-green-600' : 'text-red-600'}`}>
                          {sessionDetail.analysis.hasTitle ? <CheckCircle className="h-3 w-3" /> : <XCircle className="h-3 w-3" />}
                          Has Title
                        </div>
                        <div className={`flex items-center gap-2 ${sessionDetail.analysis.hasKeyTakeaways ? 'text-green-600' : 'text-red-600'}`}>
                          {sessionDetail.analysis.hasKeyTakeaways ? <CheckCircle className="h-3 w-3" /> : <XCircle className="h-3 w-3" />}
                          Has Takeaways
                        </div>
                        <div className={`flex items-center gap-2 ${sessionDetail.analysis.hasActionLedger ? 'text-green-600' : 'text-red-600'}`}>
                          {sessionDetail.analysis.hasActionLedger ? <CheckCircle className="h-3 w-3" /> : <XCircle className="h-3 w-3" />}
                          Has Actions
                        </div>
                        <div className={`flex items-center gap-2 ${!sessionDetail.analysis.isEmpty ? 'text-green-600' : 'text-red-600'}`}>
                          {!sessionDetail.analysis.isEmpty ? <CheckCircle className="h-3 w-3" /> : <XCircle className="h-3 w-3" />}
                          Not Empty
                        </div>
                      </div>
                    </div>

                    {/* Parse Error */}
                    {sessionDetail.debug.parseError && (
                      <div className="border-t pt-4">
                        <span className="font-medium text-red-700 block mb-2">Parse Error:</span>
                        <div className="bg-red-50 p-2 rounded text-xs text-red-600 font-mono">
                          {sessionDetail.debug.parseError}
                        </div>
                      </div>
                    )}

                    {/* Raw Data Preview */}
                    <div className="border-t pt-4">
                      <span className="font-medium text-slate-700 block mb-2">Raw Data Preview:</span>
                      <div className="bg-slate-50 p-3 rounded text-xs font-mono max-h-32 overflow-y-auto">
                        {sessionDetail.rawMomData ? (
                          <pre>{JSON.stringify(sessionDetail.rawMomData, null, 2).substring(0, 500)}...</pre>
                        ) : (
                          <span className="text-slate-500">No data</span>
                        )}
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-8 text-red-500">Failed to load session details</div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </AppLayout>
  );
}
